import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { useForm, Controller } from "react-hook-form";

import styles from "./Forms.module.css";

import PasswordInput from "../UILibrary/PasswordInput";
import Submission from "../UILibrary/Submission";
import TimeCountDown from "../UILibrary/TimeCountDown";
import VerificationInput from "../UILibrary/VerificationInput";
import ChangePasswordIcon from "Utils/Assets/SVGs/ChangePasswordIcon";

import { IResetPasswordFormData } from "@model/Authentication.model";
import { CodeChangePayload } from "@model/UIComponents.model";
import { StringOrUndefined } from "@model/GlobalModels.model";
import {
  defaultResetPasswordFormValues,
  passwordRules,
} from "Utils/Global/globalConstants";
import { validatePasswordRegExpressions } from "Utils/Global/globalfuncion";
import { useParams, useSearchParams } from "react-router-dom";
import { AuthenticationService } from "../../../Logic/Authentication/AuthenticationService";

const verificationCodeLength = 4;
const newRequestWaitTime = 60;

const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const { email: emailParam } = useParams<{ email?: string }>();
  const email =
    (emailParam ? decodeURIComponent(emailParam) : searchParams.get("email") ?? "").trim();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultResetPasswordFormValues,
  });

  const [code, setCode] = useState<string[]>(
    Array(verificationCodeLength).fill("")
  );
  const [canSubmit, setCanSubmit] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [segmentError, setSegmentError] = useState<StringOrUndefined>();
  const [loading, setLoading] = useState(false);
  const auth = new AuthenticationService();
  useEffect(() => {
    if (canSubmit) setSegmentError(undefined);
  }, [canSubmit]);

  const onCodeChanged = (payload: CodeChangePayload) => {
    setCode(payload.data);
    setCanSubmit(payload.isComplete);
  };

  const onValidReset = async (form: IResetPasswordFormData) => {
    if (!canSubmit) {
      setSegmentError(`Please enter all ${verificationCodeLength} characters`);
      setApiMessage(null);
      return;
    }

    const msg = await auth.handlePasswordResetConfirm({
      data: {
        email,
        code: code.join(""),
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword,
      },
      reset,
      setApiError,
      setLoading,
    });

    if (msg) {
      setApiError(null);
      setApiMessage(msg);
      setCode(Array(verificationCodeLength).fill(""));
    }
  };

  const onInvalidSubmit = () => {
    if (!canSubmit) {
      setSegmentError(`Please enter all ${verificationCodeLength} characters`);
    }
  };


  const requestNewCode = async (
    setActionAllowed: Dispatch<SetStateAction<boolean>>
  ) => {

    setActionAllowed(false);

    const msg = await auth.handlePasswordResetRequest({
      data: { email },
      setApiError: (m) => {
        if (m) setApiMessage(null);
        setApiError(m);
      },
      setLoading,

    });

    if (msg) {
      setApiError(null);
      setApiMessage(msg);
    }
  }


  return (
    <div className={styles.pageWrapper}>
      <div className={styles.formContainerr}>
        <form className={styles.formInner} data-testid="reset_password_form">
          <div className={styles.verificationIconWrapper}>
            <ChangePasswordIcon />
          </div>

          <h2 className={styles.formTitle} data-testid="reset_form_title">
            Reset Your Password
          </h2>

          <div className="margin-top--20">
            <p className={styles.formText} data-testid="reset_form_text">
              Enter the verification code sent to <strong>{email}</strong> and
              set your new password.
            </p>

            <VerificationInput
              length={verificationCodeLength}
              type="number"
              onChange={onCodeChanged}
              error={segmentError}
            />
          </div>

          <div className="margin-top--5">
            <TimeCountDown
              message="Request New Code"
              time={newRequestWaitTime}
              action={requestNewCode}
            />
          </div>

          <div className="margin-top--10">
            <Controller
              name="newPassword"
              control={control}
              rules={{
                required: "Password is required",
                validate: validatePasswordRegExpressions,
              }}
              render={({ field, fieldState }) => (
                <PasswordInput
                  label="Password"
                  placeholder="Enter your password"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  rules={passwordRules}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: "Password Confirmation is required",
                validate: (value) =>
                  value === control._formValues.newPassword ||
                  "Passwords do not match",
              }}
              render={({ field, fieldState }) => (
                <PasswordInput
                  label="Confirm Password"
                  placeholder="Re-enter your new password"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  rules={[]}
                />
              )}
            />

            {apiMessage && (
              <div
                className={styles.successMessage}
                data-testid="reset_success_message"
              >
                <p>{apiMessage}</p>
              </div>
            )}
          </div>

          <Submission
            buttonText="Change Password"
            onClick={handleSubmit(onValidReset, onInvalidSubmit)}
            error={apiError || undefined}
          />
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
